import React, { FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { deleteIcon, inlineMenuIcon, refreshIcon } from '@justgiving/icons';
import SVG from '@justgiving/svg';
import { captureException } from '@sentry/browser';

import {
  PublishingSource,
  TimelineEntry,
} from '../../../../../types/__generated__';
import { useApp } from '../../../../hooks';
import { useRefreshTimelineEntry } from '../../../../hooks/timeline/useRefreshTimelineEntry';
import { getBackendErrorMessage } from '../../../../utils/getBackendErrorMessage';
import {
  DropdownMenu,
  DropdownMenuItem,
} from '../../../DropdownMenu/DropdownMenu';

export type OnDeleteEntry = (id?: string) => void;

interface MenuProps {
  onDeleteEntry: OnDeleteEntry;
  timelineEntry: TimelineEntry;
}

export const Menu: FunctionComponent<MenuProps> = ({
  timelineEntry,
  onDeleteEntry,
}) => {
  const { t } = useTranslation('common', { useSuspense: false });
  const items = useMenu(timelineEntry, onDeleteEntry);

  return (
    <DropdownMenu
      ariaLabel={t('timeline.menu')}
      items={items}
      icon={<SVG markup={inlineMenuIcon} />}
    />
  );
};

function useMenu(timelineEntry: TimelineEntry, onDeleteEntry: OnDeleteEntry) {
  const { t } = useTranslation('common', { useSuspense: false });
  const { disableRefresh, handleRefresh } = useRefresh(timelineEntry);

  const items: DropdownMenuItem[] = [
    {
      key: 'delete',
      icon: deleteIcon,
      label: timelineEntry.fitnessActivity
        ? t('timeline.deleteFitnessTimelineEntry.label')
        : t('timeline.deleteNonFitnessTimelineEntry.label'),
      onClick: () => onDeleteEntry(timelineEntry.id),
    },
  ];

  const isRefreshable =
    timelineEntry.publishingSource === PublishingSource.Strava;

  if (isRefreshable) {
    items.push({
      key: 'refresh',
      icon: refreshIcon,
      label: disableRefresh
        ? t('timeline.refreshingTimelineEntry.label')
        : t('timeline.refreshTimelineEntry.label'),
      onClick: handleRefresh,
      disabled: disableRefresh,
    });
  }

  return items;
}

function useRefresh({ id, refreshRequestedTime }: TimelineEntry) {
  const { t } = useTranslation('common', { useSuspense: false });
  const { showToast } = useApp();
  const [refreshTimelineEntryMutation] = useRefreshTimelineEntry();

  const shouldDisableRefreshButton = () => {
    if (!refreshRequestedTime) return false;

    const earliestTimeToEnableRefreshButton = new Date(refreshRequestedTime);

    earliestTimeToEnableRefreshButton.setSeconds(
      earliestTimeToEnableRefreshButton.getSeconds() + 1,
    );

    return new Date(Date.now()) < earliestTimeToEnableRefreshButton;
  };

  const [disableRefresh, setDisableRefresh] = useState(
    shouldDisableRefreshButton(),
  );

  const handleRefresh = async () => {
    await refreshTimelineEntry(id, () => setDisableRefresh(true));
  };

  const refreshTimelineEntry = async (id: string, onSuccess: () => void) => {
    try {
      await refreshTimelineEntryMutation(id);

      showToast({
        type: 'success',
        message: t('timeline.refreshTimelineEntryRequested'),
        dismissAfter: 5000,
      });

      onSuccess();
    } catch (err) {
      showToast({
        type: 'warning',
        message: getBackendErrorMessage(err),
        dismissAfter: 10000,
      });

      captureException(err);
    }
  };

  return { disableRefresh, handleRefresh };
}
